import { useContext, useEffect, useRef, useState } from 'react';

import {
	Button,
	Checkbox,
	Flex,
	Form,
	Input,
	Modal,
	Select,
	Tooltip,
	Typography,
	message,
} from 'antd';
import type { SelectProps } from 'antd';
import { useForm } from 'antd/es/form/Form';

import { useUpdateCreatorInCollection } from '@/api/monitor_collections';
import prepareImageSrc from '@/lib/helpers/prepareImageSrc';
import { CollectionContext } from '@/providers/CollectionsContextProvider';

import styles from './styles.module.css';

import placeholderImage from '../../lib/assets/placeholder.jpg';

type TProps = {
	isOpen: boolean;
	setOpen: any;
	data: any;
};

export default function EditCreatorModal({
	isOpen = false,
	data = null,
	setOpen,
}: TProps) {
	const selectRef = useRef<any>(null);
	const { modalData } = useContext(CollectionContext);
	const [form] = useForm();

	const [tags, setTags] = useState<SelectProps['options']>(
		modalData?.tags?.map((keyword: string) => ({
			label: keyword,
			value: keyword,
		})) || [],
	);

	const { mutate: updateCreator, isLoading: isUpdateCreatorLoading } =
		useUpdateCreatorInCollection(modalData?.collection_id);

	const handleOk = async () => {
		try {
			await form.validateFields();

			updateCreator(
				{
					creator_id: modalData?.user_creators_collection_item_id,
					collection_id: modalData?.collection_id,
					tags: form.getFieldValue('tags'),
				},
				{
					onSuccess: () => {
						message.success('Updated');
						setOpen(false);
						form.resetFields();
					},
					onError: (error) => {
						const err = error as any;
						message.error(
							`An error occured: ${
								err?.response?.data?.message || err?.message
							}.`,
						);
						console.error('error', error);
					},
				},
			);
		} catch (error) {}
	};

	const handleCancel = () => {
		setOpen(false);
		setTags([]);
		form.resetFields();
	};

	const handleChange = (value: string[]) => {
		setTags(value?.map((item) => ({ label: item, value: item })));
		selectRef.current?.blur();
	};

	useEffect(() => {
		if (modalData?.tags) {
			setTags(
				modalData?.tags?.map((keyword: string) => ({
					label: keyword,
					value: keyword,
				})),
			);
		}
	}, [data]);

	useEffect(() => {
		form.setFieldsValue(modalData);
	}, [form, modalData]);

	return (
		<Modal
			title={'Edit creator'}
			centered
			open={isOpen}
			onOk={handleOk}
			onCancel={handleCancel}
			closable
			maskClosable={false}
			footer={[
				<Button
					key="back"
					onClick={handleCancel}
					disabled={isUpdateCreatorLoading}
				>
					Cancel
				</Button>,
				<Button
					key="submit"
					type="primary"
					loading={isUpdateCreatorLoading}
					onClick={handleOk}
					disabled={isUpdateCreatorLoading}
				>
					Save
				</Button>,
			]}
		>
			<Flex gap={4} align="center" style={{ marginBottom: '1rem' }}>
				{modalData?.profile_image_url ? (
					<img
						src={
							modalData?.profile_image_url?.startsWith('http')
								? modalData?.profile_image_url
								: prepareImageSrc(modalData?.profile_image_url)
						}
						alt={modalData?.profile_name}
						className={styles.thumbnail}
						width={36}
						height={36}
						onError={(e) => {
							// @ts-ignore
							e.target.src = placeholderImage; // Fallback image
						}}
					/>
				) : (
					<img
						src={placeholderImage}
						width={130}
						height={124}
						className={styles.thumbnail}
					/>
				)}
				{modalData?.url ? (
					<Tooltip title={modalData?.profile_name}>
						<Button
							type="link"
							size="large"
							href={modalData?.url}
							target="_blank"
							style={{
								width: 'fit-content',
								fontSize: '14px',
								display: 'flex',
								wordWrap: 'break-word',
								overflow: 'hidden',
								padding: '2px',
							}}
						>
							<span
								style={{
									width: '100%',
									fontSize: '14px',
									display: 'inline-block',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
								}}
							>
								{modalData?.profile_name}
							</span>
						</Button>
					</Tooltip>
				) : (
					<Tooltip title={modalData?.profile_name}>
						<Typography.Text
							style={{
								width: 'fit-content',
								fontSize: '14px',
								display: 'inline-block',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
							}}
						>
							{modalData?.profile_name}
						</Typography.Text>
					</Tooltip>
				)}
			</Flex>
			<Form
				form={form}
				initialValues={
					{
						// isActive: isActive,
						// keywords,
					}
				}
				style={{ marginBottom: '1rem' }}
			>
				<Flex vertical gap={16}>
					{data?.active && (
						<Form.Item
							// label="Active"
							name="isActive"
							valuePropName="checked"
							// wrapperCol={{ offset: 6, span: 16 }}
							// onMetaChange={}
							style={{ margin: 0 }}
						>
							<Checkbox>Active</Checkbox>
						</Form.Item>
					)}

					<Form.Item name="tags" style={{ margin: 0 }}>
						<Select
							mode="tags"
							style={{ width: '100%' }}
							placeholder="Search tags"
							onChange={handleChange}
							options={tags}
							disabled={isUpdateCreatorLoading}
							ref={selectRef}
						/>
					</Form.Item>
				</Flex>
			</Form>
		</Modal>
	);
}
